import type { CustomerSnapshotInput } from '../invoice';
import type { PaginationMeta } from '../journalEntry';
import type { Item } from '../item';

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'SENT'
  | 'ACKNOWLEDGED'
  | 'CLOSED'
  | 'CANCELLED';

export const PURCHASE_ORDER_STATUSES: PurchaseOrderStatus[] = [
  'DRAFT',
  'SENT',
  'ACKNOWLEDGED',
  'CLOSED',
  'CANCELLED',
];

export interface PurchaseOrderVendorSnapshot {
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export interface PurchaseOrderVendorRef {
  id: number;
  display_name: string;
}

export interface PurchaseOrderItemSnapshot {
  name: string;
  sku: string;
  unit_price: string;
}

export interface PurchaseOrderLine {
  id: number;
  item_id: number;
  item_snapshot: PurchaseOrderItemSnapshot;
  line_number: number;
  description: string | null;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  tax_amount: string;
  line_total: string;
  item?: Item | null;
}

export interface PurchaseOrder {
  id: number;
  vendor_id: number;
  po_number: string;
  vendor_snapshot: PurchaseOrderVendorSnapshot;
  po_date: string;
  expected_delivery_date: string | null;
  ship_to: string | null;
  sub_total: string;
  discount_total: string;
  tax_total: string;
  total_amount: string;
  status: PurchaseOrderStatus;
  memo: string | null;
  notes: string | null;
  lines?: PurchaseOrderLine[];
  vendor?: PurchaseOrderVendorRef | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePurchaseOrderLineData {
  item_id: number;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
  tax_amount?: number;
}

export interface CreatePurchaseOrderData {
  vendor_id: number;
  vendor_snapshot?: CustomerSnapshotInput;
  po_date: string;
  expected_delivery_date?: string | null;
  ship_to?: string | null;
  status?: PurchaseOrderStatus;
  memo?: string | null;
  notes?: string | null;
  lines: CreatePurchaseOrderLineData[];
}

export interface UpdatePurchaseOrderData {
  vendor_id?: number;
  vendor_snapshot?: CustomerSnapshotInput;
  po_date?: string;
  expected_delivery_date?: string | null;
  ship_to?: string | null;
  status?: PurchaseOrderStatus;
  memo?: string | null;
  notes?: string | null;
  lines?: CreatePurchaseOrderLineData[];
}

export interface SendPurchaseOrderData {
  to?: string;
  cc?: string | string[];
  subject?: string;
  message?: string;
}

export interface GetPurchaseOrdersParams {
  search?: string;
  vendor_id?: number;
  status?: string;
  po_date_from?: string;
  po_date_to?: string;
  sort?: string;
  per_page?: number;
  page?: number;
}

export interface PurchaseOrdersResult {
  purchaseOrders: PurchaseOrder[];
  meta: PaginationMeta;
}

export interface PurchaseOrdersResponse {
  data: PurchaseOrder[];
  meta: PaginationMeta;
}

export interface PurchaseOrderResponse {
  data: PurchaseOrder;
}

export const getPurchaseOrderVendorName = (po: PurchaseOrder): string =>
  po.vendor?.display_name ?? po.vendor_snapshot.name ?? '—';
